import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

interface LoadingScreenProps {
    isLoading: boolean;
}

const LoadingScreen = ({ isLoading }: LoadingScreenProps) => {
    const [isAnimating, setIsAnimating] = useState(true);

    // Handle exit animation
    useEffect(() => {
        if (!isLoading) {
            const timer = setTimeout(() => {
                setIsAnimating(false);
            }, 600);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    // Enhanced animation variants
    const containerVariants = {
        loading: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3,
                delayChildren: 0.2,
            },
        },
        exit: {
            opacity: 0,
            transition: {
                staggerChildren: 0.1,
                staggerDirection: -1,
                when: "afterChildren",
            },
        },
    };

    const leftBracketVariants = {
        initial: { 
            opacity: 0, 
            x: -100,
            scale: 0.5,
            rotateY: -90
        },
        loading: { 
            opacity: 1, 
            x: 0,
            scale: 1,
            rotateY: 0,
            transition: {
                duration: 0.7,
                ease: [0.22, 1, 0.36, 1],
            }
        },
        exit: {
            opacity: 0,
            x: -50,
            rotateY: -90,
            transition: {
                duration: 0.4,
            },
        },
    };

    const rightBracketVariants = {
        initial: { 
            opacity: 0, 
            x: 100,
            scale: 0.5,
            rotateY: 90
        },
        loading: { 
            opacity: 1, 
            x: 0,
            scale: 1,
            rotateY: 0,
            transition: {
                duration: 0.7,
                ease: [0.22, 1, 0.36, 1],
            }
        },
        exit: {
            opacity: 0,
            x: 50,
            rotateY: 90,
            transition: {
                duration: 0.4,
            },
        },
    };

    const slashVariants = {
        initial: { 
            opacity: 0, 
            y: -50,
            scale: 0,
            rotate: -45
        },
        loading: { 
            opacity: 1, 
            y: 0,
            scale: 1,
            rotate: 0,
            transition: {
                delay: 0.8,
                duration: 0.5,
                type: "spring",
                stiffness: 200,
                damping: 15
            }
        },
        exit: {
            opacity: 0,
            y: 30,
            transition: {
                duration: 0.3,
            },
        },
    };

    const pulseVariants = {
        initial: { scale: 1, opacity: 0.7 },
        pulse: {
            scale: [1, 1.2, 1],
            opacity: [0.7, 1, 0.7],
            transition: {
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
            },
        },
    };

    const particleVariants = {
        initial: { 
            opacity: 0, 
            y: 0, 
            scale: 0.8,
            rotate: 0
        },
        animate: (i: number) => ({
            opacity: [0, 0.8, 0],
            y: [-20, -120 - Math.random() * 50],
            scale: [0.8, 1.2, 0.9],
            rotate: [0, Math.random() * 20 - 10],
            transition: { 
                duration: 4 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 8,
                ease: "easeOut",
            }
        })
    };

    // If not loading and animation finished, don't render anything
    if (!isLoading && !isAnimating) return null;

    return (
        <motion.div
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/95 backdrop-blur-sm"
            initial={{ opacity: 1 }}
            animate={{ opacity: isLoading ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
        >
            {/* Enhanced background effects */}
            <motion.div
                className="absolute inset-0 overflow-hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
                {/* Gradient orbs */}
                <motion.div
                    className="absolute top-1/4 left-1/4 w-1/2 h-1/2 rounded-full bg-crimson/10 blur-[100px]"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3],
                        x: [0, 20, 0],
                        y: [0, -20, 0],
                    }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                />
                <motion.div
                    className="absolute bottom-1/4 right-1/4 w-1/3 h-1/3 rounded-full bg-blue-500/10 blur-[80px]"
                    animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.3, 0.4, 0.3],
                        x: [0, -20, 0],
                        y: [0, 20, 0],
                    }}
                    transition={{
                        duration: 10,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1,
                    }}
                />
            </motion.div>

            <motion.div
                className="relative flex items-center perspective-1000"
                variants={containerVariants}
                initial="initial"
                animate={isLoading ? "loading" : "exit"}
            >
                {/* Enhanced background pulse effect */}
                <motion.div
                    className="absolute -inset-20 rounded-full bg-gradient-to-r from-crimson/10 via-blue-500/10 to-crimson/10 blur-3xl"
                    variants={pulseVariants}
                    initial="initial"
                    animate="pulse"
                />

                {/* Code symbols with 3D effect */}
                <motion.div
                    variants={leftBracketVariants}
                    className="text-8xl md:text-9xl font-bold text-crimson transform-gpu"
                    style={{ transformStyle: "preserve-3d" }}
                >
                    &lt;
                </motion.div>

                <motion.div
                    variants={slashVariants}
                    className="text-8xl md:text-9xl font-bold text-foreground mx-2 transform-gpu"
                    style={{ transformStyle: "preserve-3d" }}
                >
                    /
                </motion.div>

                <motion.div
                    variants={rightBracketVariants}
                    className="text-8xl md:text-9xl font-bold text-crimson transform-gpu"
                    style={{ transformStyle: "preserve-3d" }}
                >
                    &gt;
                </motion.div>
            </motion.div>

            {/* Enhanced code effect particles */}
            <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
                {[...Array(30)].map((_, i) => (
                    <motion.div
                        key={`particle-${i}`}
                        className="absolute text-sm font-mono text-crimson"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                        custom={i}
                        variants={particleVariants}
                        initial="initial"
                        animate="animate"
                    >
                        {[
                            '{ }', '</>', '()', '[]', '&&', '||', '=>', '++', '==', '!=', 
                            'const', '.then()', '.map()', 'import', 'React', 'useEffect',
                            'useState', 'props', 'return', 'export', 'async', 'await',
                            'try', 'catch', 'finally', 'class', 'extends', 'super',
                            'static', 'private'
                        ][Math.floor(Math.random() * 30)]}
                    </motion.div>
                ))}
            </div>
        </motion.div>
    );
};

export default LoadingScreen; 